<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<template>
  <div
    :class="[computedClass, { hidden: !richText }]"
    @click="handleClick"
    v-html="richText"
  />
</template>

<script setup>
import { computed } from 'vue'
import handleHashLink from '../utils/handleHashLink'
import { renderRichText } from '@storyblok/vue'
import { useColorPalette } from '../composables/colorPalette'

const props = defineProps({
  blok: {
    type: Object,
    default: () => null,
  },
  richTextContent: {
    type: [String, Object, Boolean],
    default: false,
  },
  customTextColor: {
    type: Boolean,
    default: false,
  },
  resetStyles: {
    type: Boolean,
    default: false,
  },
  inline: {
    type: Boolean,
    default: false,
  },
})

const { textColorClass, colorName, isBackgroundDark } = useColorPalette()

const richText = computed(() => {
  const text = props.richTextContent || props.blok.textContent

  if (text) {
    if (typeof text === 'string') {
      return text
    }
    const renderedText = renderRichText(text)
    // Don't show the rich text for an empty paragraph
    return !renderedText || renderedText === '<p></p>' ? '' : renderedText
  }

  return ''
})

const listClass = computed(() => {
  if (props.blok) {
    const listBulletType = props.blok.listBulletType || 'triangle-bullet'
    // checkmark heavy-checkmark circle-bullet triangle-bullet arrowhead chevron-right
    const listBulletColor = `list-${colorName(
      props.blok.listBulletColor,
      props.blok.bgVariant,
    )}`

    return `jb-list ${listBulletColor} ${listBulletType}`
  }
  return ''
})

const linkClass = computed(() => {
  return `richtext-link ${colorName(props.blok.linkTextColor || props.blok.textVariant, props.blok.bgVariant)}`
})

const computedClass = computed(() => {
  let classes = ''

  if (props.inline) {
    classes = 'inline rich-text-inline'
  } else {
    classes = 'block'
  }

  if (props.resetStyles) {
    return `grid gap-2 ${classes}`
  }

  const variantClass = isBackgroundDark(props.blok.bgVariant)
    ? 'prose-white prose-invert'
    : 'prose-blue'

  return `${classes} paragraph prose prose-lg ${listClass.value} ${
    props.customTextColor
      ? ''
      : textColorClass(props.blok.textVariant, props.blok.bgVariant)
  } ${variantClass} ${linkClass.value}`
})

const handleClick = (event) => {
  let link = event.target
  const isValidTarget = (link) => link.tagName === 'A'

  while (!isValidTarget(link) && link !== event.currentTarget) {
    link = link.parentNode
  }

  handleHashLink(event, link)
}
</script>
